import { httpPost, httpPatchMerge, httpDelete } from './http'
import { getSuppliersFromOpportunity } from './opportunities'
import { getContactsForCompanyIdPreProcessed } from './companies'
import { getContactsForSupplierIdPreProcessed } from './suppliers'
import type { IOpportunityContact } from '~/types/opportunity/models'
import type { TEntityId, IEntitySelectItem } from '~/types/models'
import { Contact } from '~/cynqueModules/dataObjects/Contact'

const apiEntityPath = '/api/opportunitycontacts'

/**
 * Candidate contacts for the "add contact" picker on an opportunity: the opportunity's
 * company contacts plus the contacts of every supplier used by its MSServices. The
 * suppliers are fetched by opportunity id (they are not a parent of an opportunityContact,
 * so they are not carried on the parent object). companyId comes from the parent chain
 * (opportunityContact -> opportunity -> company).
 */
export async function getAddableContactsForOpportunityPreProcessed(
  opportunityId: TEntityId,
  companyId: TEntityId
) {
  // getSuppliersFromOpportunity is (mis)typed as IOpportunity but returns the supplier
  // list at runtime (mock/API return { data: <supplier list> }); cast to the real shape.
  const suppliers = (await getSuppliersFromOpportunity(
    opportunityId
  )) as unknown as IEntitySelectItem[]
  const supplierIds = (suppliers ?? []).map((s) => s.id)

  const [companyContacts, ...supplierContactLists] = await Promise.all([
    companyId
      ? getContactsForCompanyIdPreProcessed(companyId)
      : Promise.resolve([]),
    ...supplierIds.map((id) => getContactsForSupplierIdPreProcessed(id)),
  ])
  // Re-run the generic Contact() factory so the picker's `namePlusBiz` label reads
  // "Name | company/supplier | role" (the CompanyContact/SupplierContact factories build
  // it as "Name - Parent - phone" instead).
  return [...companyContacts, ...supplierContactLists.flat()].map((c) =>
    Contact(c)
  )
}

export async function createOpportunityContact(entity: IOpportunityContact) {
  const callingMn = createOpportunityContact.name
  const apiPath = apiEntityPath
  return httpPost<IOpportunityContact>({
    callingMn,
    apiPath,
    entity,
  })
}

export async function updateOpportunityContact(
  entityData: Partial<IOpportunityContact>,
  id: TEntityId
) {
  const callingMn = updateOpportunityContact.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialOpportunityContact = Partial<IOpportunityContact>
  return httpPatchMerge<TPartialOpportunityContact>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function deleteOpportunityContact(
  entityData: IOpportunityContact
) {
  const callingMn = deleteOpportunityContact.name
  const apiPath = apiEntityPath
  return httpDelete<IOpportunityContact>({
    callingMn,
    apiPath,
    entityData,
  })
}
